import { useState } from "react";
import { comics, ranking, type Comic, type RankItem } from "@/mocks/comics";
import SectionHeading from "./SectionHeading";

type Tab = "hot" | "rating" | "new";

interface RowData {
  rank: number;
  comic: Comic;
  trend: string;
  metric: string;
}

function toRows(items: { comic: Comic; trend: string; metric: string }[]): RowData[] {
  return items.map((it, i) => ({ rank: i + 1, ...it }));
}

const tabs: { key: Tab; label: string }[] = [
  { key: "hot", label: "人气榜" },
  { key: "rating", label: "口碑榜" },
  { key: "new", label: "新作榜" },
];

export default function Ranking() {
  const [tab, setTab] = useState<Tab>("hot");

  const hotRows: RowData[] = ranking.map((r: RankItem) => ({
    rank: r.rank,
    comic: r.comic,
    trend: r.trend,
    metric: `${r.hot}万热`,
  }));

  const ratingRows: RowData[] = toRows(
    [...comics]
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 8)
      .map((c, i) => ({ comic: c, trend: "稳定", metric: `${c.rating} 分` })),
  );

  const newRows: RowData[] = toRows(
    [...comics]
      .sort((a, b) => a.episodes - b.episodes)
      .slice(0, 8)
      .map((c, i) => ({ comic: c, trend: i === 0 ? "新入" : "上升", metric: `${c.episodes} 话` })),
  );

  const data = tab === "hot" ? hotRows : tab === "rating" ? ratingRows : newRows;

  return (
    <section id="ranking" className="relative py-14 md:py-20 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="Ranking 人气榜单"
          title="本周韩国动态漫画人气榜单"
          desc="综合人气、评分与读者讨论热度实时更新，快速锁定当下最值得追的高口碑韩漫。"
        />

        {/* tab switcher */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-background-50 border border-background-200 rounded-full p-1 gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-5 py-2 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-colors ${
                  tab === t.key
                    ? "bg-primary-500 text-background-50 shadow-sm"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* list */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {data.map((row) => {
            const top3 = row.rank <= 3;
            const isHot = tab === "hot";
            return (
              <article
                key={row.comic.id}
                className="group flex items-center gap-4 p-3 md:p-4 rounded-2xl bg-background-50 border border-background-200 hover:border-primary-300 hover:-translate-y-0.5 transition-all"
              >
                {/* rank */}
                <div
                  className={`w-12 h-14 md:w-14 md:h-16 flex items-center justify-center rounded-xl font-display text-2xl md:text-3xl shrink-0 ${
                    top3 ? "bg-accent-500 text-foreground-950" : "bg-background-200 text-foreground-500"
                  }`}
                >
                  {row.rank}
                </div>

                {/* thumb */}
                <a href="#featured" className="shrink-0">
                  <img
                    src={row.comic.cover}
                    alt={`${row.comic.title} 韩国动态漫画海报`}
                    className="w-14 h-20 md:w-16 md:h-[88px] object-cover object-top rounded-lg"
                  />
                </a>

                {/* info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base md:text-lg font-bold text-foreground-950 truncate">
                      {row.comic.title}
                    </h3>
                    {top3 && <i className="ri-vip-crown-2-fill text-accent-500"></i>}
                  </div>
                  <p className="text-xs text-foreground-500 mt-0.5">{row.comic.author} · {row.comic.genre}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {row.comic.tags.slice(0, 2).map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800 text-[11px] font-medium">
                        {t}
                      </span>
                    ))}
                    <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-800 text-[11px] font-bold">
                      {row.metric}
                    </span>
                  </div>
                </div>

                {/* trend + score */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-xl md:text-2xl font-black text-primary-500">{row.comic.rating.toFixed(1)}</span>
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold ${
                      isHot && row.trend === "持平"
                        ? "text-foreground-400"
                        : isHot && row.trend.includes("新")
                        ? "text-accent-600"
                        : isHot && row.trend.startsWith("+")
                        ? "text-primary-600"
                        : "text-secondary-600"
                    }`}
                  >
                    {isHot ? (
                      <>
                        <i className={`${row.trend.startsWith("+") ? "ri-arrow-up-line" : row.trend.includes("新") ? "ri-sparkling-line" : "ri-subtract-line"}`}></i>
                        {row.trend}
                      </>
                    ) : (
                      <i className="ri-arrow-up-line"></i>
                    )}
                  </span>
                </div>
              </article>
            );
          })}
        </div>

        <p className="text-center text-xs text-foreground-500 mt-8">
          榜单数据综合各平台人气与评分，每周更新 · 点击榜单作品可在精选区查看更多详情
        </p>
      </div>
    </section>
  );
}